import React from "react";
import { Button } from "./ui/Button";
import { motion } from "framer-motion";

interface ProductCardProps {
  name: string;
  description: string;
  specs: { label: string; value: string }[];
  tags: string[];
  image: string;
  onLearnMoreClick?: () => void;
  index?: number;
  enableAnimation?: boolean;
  /** When false, card height grows with content (no inner scroll). Default true. */
  scrollable?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  name,
  description,
  specs,
  tags,
  image,
  onLearnMoreClick,
  index = 0,
  enableAnimation = false,
  scrollable = true,
}) => {
  const cardContent = (
    <div
      className={`bg-[#FAFAFA] rounded-2xl overflow-hidden relative z-0 ${scrollable ? "h-auto lg:h-[450px]" : "h-auto"}`}
    >
      {/* Inner flex container - no card padding; image has 0 margin, content has padding */}
      <div className={`flex flex-col lg:flex-row items-stretch gap-0 ${scrollable ? "h-full" : "lg:min-h-0"}`}>
        {/* Image Side - 50% width, no margin/padding; rounded corners on card edge */}
        <div className={`lg:w-1/2 flex-shrink-0 h-[250px] overflow-hidden rounded-t-2xl lg:rounded-t-none lg:rounded-l-2xl ${scrollable ? "lg:h-full" : "lg:h-auto lg:min-h-[200px]"}`}>
          <img
            src={image}
            alt={name}
            className="w-full h-full object-fill"
          />
        </div>

        {/* Content Side - 50% width, padding only on text part */}
        <div className={`flex flex-col p-4 lg:p-5 ${scrollable ? "h-full lg:overflow-hidden" : ""}`}>
          {/* Scrollable content area when scrollable=true; otherwise content flows */}
          <div className={`flex-1 flex flex-col pr-1 space-y-3 ${scrollable ? "min-h-0 lg:overflow-y-auto" : ""}`}>
            <h3
              className="text-2xl sm:text-[30px] font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent flex-shrink-0"
            >
              {name}
            </h3>
            <p className="text-gray-600 text-sm sm:text-base flex-shrink-0 line-clamp-3 font-raleway">
              {description}
            </p>

            <div className="flex-shrink-0">
              <h4 className="text-lg sm:text-xl font-open-sans-hebrew font-bold text-black mb-2 sm:mb-3">
                Key Specs:
              </h4>
              <div className="space-y-1.5">
                {specs.map((spec, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-end border-b border-gray-200 last:border-0 text-body pb-1.5 font-raleway"
                  >
                    <span className="text-gray-600 font-normal text-sm sm:text-base">
                      {spec.label}
                    </span>
                    <span className="font-semibold text-black text-right text-sm sm:text-base">
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 flex-shrink-0">
              {tags.slice(0, 4).map((tag, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 font-medium font-raleway flex items-center gap-1.5 shadow-sm"
                >
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span className="line-clamp-1">{tag}</span>
                </span>
              ))}
              {/* {tags.length > 4 && (
                <span className="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-500 font-raleway flex items-center gap-1.5 shadow-sm">
                  +{tags.length - 4} more
                </span>
              )} */}
            </div>
          </div>

          {/* Fixed button at bottom */}
          <div className={`flex-shrink-0 mt-4 pt-3 border-t border-gray-200 ${scrollable ? "lg:mt-auto" : ""}`}>
            <Button
              variant="red"
              onClick={onLearnMoreClick}
              className="w-full sm:w-auto text-sm font-raleway"
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (enableAnimation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="relative z-0"
      >
        {cardContent}
      </motion.div>
    );
  }

  return cardContent;
};
